import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Play, Pause, ArrowCounterClockwise, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import Pallet3D from "@/components/Pallet3D";
import { buildLoadingSequence, buildSimulationPallet } from "@/lib/loadingSimulation";
import { loadingUnitLabel, loadingUnitName } from "@/lib/loadingUnits";

const SPEEDS = [1, 2, 4];

export default function LoadingSimulationLauncher() {
  const location = useLocation();
  const match = location.pathname.match(/^\/plans\/([^/]+)$/);
  const planId = match?.[1] || null;
  const [plan, setPlan] = useState(null);
  const [open, setOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [completed, setCompleted] = useState(0);
  const [progress, setProgress] = useState(0);
  const progressRef = useRef(0);

  useEffect(() => { progressRef.current = progress; }, [progress]);

  useEffect(() => {
    if (!planId) {
      setPlan(null);
      setOpen(false);
      return undefined;
    }
    let alive = true;
    api.get(`/plans/${planId}`)
      .then((r) => { if (alive) setPlan(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [planId]);

  const sequence = useMemo(() => buildLoadingSequence(plan), [plan]);
  const unitLabel = loadingUnitLabel(plan);
  const palletJumpOptions = useMemo(() => (plan?.pallets || []).map((pallet, palletIndex) => {
    const first = sequence.findIndex((entry) => entry.palletIndex === palletIndex);
    return {
      palletIndex,
      palletNo: pallet.pallet_no ?? palletIndex + 1,
      unitName: loadingUnitName(plan, pallet, palletIndex),
      first,
    };
  }).filter((item) => item.first >= 0), [plan, sequence]);
  const simulation = useMemo(
    () => buildSimulationPallet(plan, sequence, completed, progress),
    [plan, sequence, completed, progress]
  );

  useEffect(() => {
    if (!open || !playing || completed >= sequence.length) return undefined;
    const duration = 1100 / speed;
    let raf = 0;
    const startAt = performance.now() - progressRef.current * duration;
    const tick = (now) => {
      const next = Math.min(1, (now - startAt) / duration);
      setProgress(next);
      if (next >= 1) {
        setCompleted((c) => c + 1);
        setProgress(0);
        progressRef.current = 0;
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [open, playing, completed, sequence.length, speed]);

  useEffect(() => {
    if (sequence.length && completed >= sequence.length) setPlaying(false);
  }, [completed, sequence.length]);

  useEffect(() => {
    if (!planId) return undefined;
    let alive = true;
    const openFromPlan = async () => {
      setCompleted(0);
      setProgress(0);
      progressRef.current = 0;
      setPlaying(false);
      try {
        const r = await api.get(`/plans/${planId}`);
        if (!alive) return;
        setPlan(r.data);
        setOpen(true);
      } catch (_) {
        // PlanDetail already owns the user-facing error surface; do not create a
        // second noisy toast here. A later open attempt can retry the request.
      }
    };
    window.addEventListener("h3poe:open-loading-simulation", openFromPlan);
    return () => {
      alive = false;
      window.removeEventListener("h3poe:open-loading-simulation", openFromPlan);
    };
  }, [planId]);

  if (!planId || !plan || plan.status !== "approved") return null;

  const replay = () => {
    setCompleted(0);
    setProgress(0);
    progressRef.current = 0;
    setPlaying(true);
  };

  const jumpToPallet = (firstIndex) => {
    setPlaying(false);
    setCompleted(firstIndex);
    setProgress(0);
    progressRef.current = 0;
  };

  const currentEntry = sequence[Math.min(completed, Math.max(0, sequence.length - 1))];
  const currentPalletNo = simulation?.pallet?.pallet_no ?? currentEntry?.palletNo ?? 1;
  const currentUnit = (plan.pallets || []).find((p) => String(p.pallet_no) === String(currentPalletNo));
  const currentUnitName = loadingUnitName(plan, currentUnit, Math.max(0, Number(currentPalletNo) - 1));
  const activeSku = simulation?.active?.box?.sku || (completed >= sequence.length ? "Tamamlandı" : "—");

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-[100] bg-slate-950/80 backdrop-blur-sm p-3 sm:p-6" data-testid="loading-simulation-modal">
          <div className="h-full max-w-7xl mx-auto bg-white border border-slate-700 shadow-2xl flex flex-col overflow-hidden">
            <div className="px-4 sm:px-6 py-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-lg font-display font-extrabold text-slate-900">Yükleme Simülasyonu</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Plan {plan.plan_no} · Planlanmış yükleme sırası kayıtlı final yerleşiminden deterministik olarak üretilir; tarihsel operatör sırası değildir.
                </div>
              </div>
              <button onClick={() => { setOpen(false); setPlaying(false); }} className="p-2 hover:bg-slate-100" aria-label="Kapat"><X size={20} /></button>
            </div>

            <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[1fr_300px]">
              <div className="min-h-[420px] bg-slate-950 relative simulation-viewer">
                {simulation?.pallet ? (
                  <Pallet3D
                    pallet={simulation.pallet}
                    L={simulation.pallet.L || plan.pallet_L}
                    W={simulation.pallet.W || plan.pallet_W}
                    H={simulation.pallet.H || plan.pallet_H}
                    products={plan.products || {}}
                    onSelectBox={() => {}}
                    hiddenLayerKeys={[]}
                  />
                ) : <div className="h-full grid place-items-center text-white">Simülasyon verisi yok</div>}
                <style>{`
                  .simulation-viewer [data-testid="toggle-drag"],
                  .simulation-viewer [data-testid="toggle-area-select"] { display:none !important; }
                `}</style>
              </div>

              <aside className="border-l border-slate-200 bg-slate-50 p-4 sm:p-5 space-y-5 overflow-y-auto">
                <div>
                  <div className="text-[10px] uppercase text-slate-400 mb-1">{unitLabel} Seç</div>
                  <select
                    data-testid="simulation-pallet-select"
                    value={String(currentPalletNo)}
                    onChange={(e) => {
                      const target = palletJumpOptions.find((p) => String(p.palletNo) === e.target.value);
                      if (target) jumpToPallet(target.first);
                    }}
                    className="w-full bg-white border border-slate-300 px-3 py-2 text-sm font-mono font-semibold"
                  >
                    {palletJumpOptions.map((p) => <option key={p.palletIndex} value={String(p.palletNo)}>{p.unitName}</option>)}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-white border border-slate-200 p-3"><div className="text-[10px] uppercase text-slate-400">{unitLabel}</div><div className="font-mono font-bold text-lg">{currentUnitName}</div></div>
                  <div className="bg-white border border-slate-200 p-3"><div className="text-[10px] uppercase text-slate-400">İlerleme</div><div className="font-mono font-bold text-lg">{Math.min(completed, sequence.length)}/{sequence.length}</div></div>
                </div>

                <div className="bg-white border border-slate-200 p-4">
                  <div className="text-[10px] uppercase text-slate-400">Şu an yerleşen SKU</div>
                  <div className="font-mono font-extrabold text-xl mt-1 break-all">{activeSku}</div>
                  {simulation?.active && <div className="text-xs text-slate-500 mt-2">Hedef: X {simulation.active.box.x} · Y {simulation.active.box.y} · Z {simulation.active.box.z} cm</div>}
                </div>

                <div className="space-y-2">
                  <div className="flex gap-2">
                    <button
                      data-testid="simulation-play-pause"
                      onClick={() => setPlaying((v) => !v)}
                      disabled={!sequence.length || completed >= sequence.length}
                      className="flex-1 inline-flex items-center justify-center gap-2 bg-slate-900 text-white py-2.5 font-semibold disabled:opacity-40"
                    >
                      {playing ? <><Pause size={16} weight="fill" /> Duraklat</> : <><Play size={16} weight="fill" /> Oynat</>}
                    </button>
                    <button data-testid="simulation-replay" onClick={replay} className="px-3 border border-slate-300 bg-white hover:bg-slate-100" title="Baştan oynat"><ArrowCounterClockwise size={18} /></button>
                  </div>
                  <div className="flex gap-1" data-testid="simulation-speed">
                    {SPEEDS.map((s) => <button key={s} onClick={() => setSpeed(s)} className={`flex-1 py-2 text-xs font-bold border ${speed === s ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-700 border-slate-300"}`}>{s}x</button>)}
                  </div>
                </div>

                <div>
                  <div className="h-2 bg-slate-200 overflow-hidden"><div className="h-full bg-violet-600 transition-all" style={{ width: `${sequence.length ? ((completed + progress) / sequence.length) * 100 : 0}%` }} /></div>
                  <div className="text-[10px] text-slate-400 mt-1 text-right">%{sequence.length ? Math.min(100, ((completed + progress) / sequence.length) * 100).toFixed(0) : 0}</div>
                </div>

                <div className="text-xs text-slate-500 leading-relaxed border-t border-slate-200 pt-4">
                  Simülasyon yalnızca görselleştirme yapar. Planı, kutu koordinatlarını veya reçeteyi değiştirmez. Sıra; {unitLabel.toLocaleLowerCase("tr-TR")} → Z → Y → X → kayıtlı kutu indeksine göre oluşturulur.
                </div>
              </aside>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
